package er.prateek.crossly.game.math

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.data.model.SavedCellFill
import er.prateek.crossly.data.model.math.MathCell
import er.prateek.crossly.data.model.math.MathCellKind
import er.prateek.crossly.data.model.math.MathPuzzle
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.CellStatus

/** Runtime state of one grid square. Only editable NUMBER cells accept input. */
class MathCellState(
    val pos: CellPos,
    val kind: MathCellKind,
    val solution: String,
    /** Glyph shown for OPERATOR/EQUALS cells, or the value for a given NUMBER cell. */
    val fixedText: String,
    val given: Boolean,
) {
    val isNumber: Boolean = kind == MathCellKind.NUMBER
    val editable: Boolean = isNumber && !given

    var userText: String by mutableStateOf(if (isNumber) (if (given) solution else "") else fixedText)
    var status: CellStatus by mutableStateOf(
        when {
            !isNumber -> CellStatus.GIVEN
            given -> CellStatus.GIVEN
            else -> CellStatus.EMPTY
        },
    )

    val isResolved: Boolean get() = isNumber && userText == solution
}

/**
 * Pure game-state machine for one [MathPuzzle]: multi-digit entry into the blank number cells,
 * check/reveal, and win detection. Mirrors `CrosswordEngine`'s shape (observable state, private
 * setters, input methods, an `isSolved` flag) so screens drive it the same way.
 */
class MathCrosswordEngine(val puzzle: MathPuzzle) {
    val width = puzzle.width
    val height = puzzle.height

    private val cellByPos: Map<CellPos, MathCellState> = buildCells(puzzle)
    val cells: List<MathCellState> = cellByPos.values.toList()

    /** Longest answer in this grid -- the per-cell input cap. */
    private val maxLen: Int = cells.filter { it.isNumber }.maxOfOrNull { it.solution.length } ?: 1

    private val editableOrder: List<CellPos> =
        cells.filter { it.editable }.map { it.pos }.sortedWith(compareBy({ it.row }, { it.col }))

    var selected: CellPos? by mutableStateOf(editableOrder.firstOrNull())
        private set
    var hintsUsed: Int by mutableStateOf(0)
        private set
    var mistakes: Int by mutableStateOf(0)
        private set
    var isSolved: Boolean by mutableStateOf(false)
        private set

    fun cellAt(pos: CellPos): MathCellState? = cellByPos[pos]

    fun selectCell(pos: CellPos) {
        if (cellByPos[pos]?.editable == true) selected = pos
    }

    fun appendDigit(ch: Char) {
        if (!ch.isDigit()) return
        val cell = selected?.let { cellByPos[it] } ?: return
        if (!cell.editable) return
        // A fresh keystroke on an already-checked/revealed cell starts the number over.
        if (cell.status == CellStatus.CORRECT || cell.status == CellStatus.INCORRECT || cell.status == CellStatus.REVEALED) {
            cell.userText = ""
        }
        if (cell.userText.length >= maxLen) return
        cell.userText += ch
        cell.status = CellStatus.FILLED
        refreshSolvedState()
    }

    /** Drag-and-drop: selects [pos] and puts [ch] there as a fresh one-digit entry. */
    fun placeDigitAt(pos: CellPos, ch: Char) {
        if (!ch.isDigit() || cellByPos[pos]?.editable != true) return
        selected = pos
        cellByPos.getValue(pos).userText = ""
        appendDigit(ch)
    }

    fun backspace() {
        val cell = selected?.let { cellByPos[it] } ?: return
        if (!cell.editable) return
        if (cell.userText.isNotEmpty()) {
            cell.userText = cell.userText.dropLast(1)
            cell.status = if (cell.userText.isEmpty()) CellStatus.EMPTY else CellStatus.FILLED
            refreshSolvedState()
        }
    }

    fun clearSelected() {
        val cell = selected?.let { cellByPos[it] } ?: return
        if (!cell.editable) return
        cell.userText = ""
        cell.status = CellStatus.EMPTY
        refreshSolvedState()
    }

    fun moveSelection(forward: Boolean) {
        if (editableOrder.isEmpty()) return
        val cur = selected?.let { editableOrder.indexOf(it) } ?: -1
        val next = if (forward) (cur + 1) % editableOrder.size
        else (cur - 1 + editableOrder.size) % editableOrder.size
        selected = editableOrder[next]
    }

    fun check() {
        cells.filter { it.editable && it.userText.isNotEmpty() }.forEach { cell ->
            if (cell.userText == cell.solution) {
                cell.status = CellStatus.CORRECT
            } else {
                cell.status = CellStatus.INCORRECT
                mistakes++
            }
        }
    }

    fun revealSelected() {
        selected?.let { revealCell(it) }
    }

    fun revealCell(pos: CellPos) {
        val cell = cellByPos[pos] ?: return
        if (!cell.editable) return
        cell.userText = cell.solution
        cell.status = CellStatus.REVEALED
        hintsUsed++
        refreshSolvedState()
    }

    private fun refreshSolvedState() {
        isSolved = cells.filter { it.isNumber }.all { it.userText == it.solution }
    }

    fun snapshotFills(): List<SavedCellFill> =
        cells.mapNotNull { cell ->
            if (!cell.editable || cell.userText.isEmpty()) null
            else SavedCellFill(cell.pos.row, cell.pos.col, cell.userText, cell.status.name)
        }

    fun applySnapshot(
        fills: List<SavedCellFill>,
        hintsUsed: Int,
        mistakes: Int,
        selectedRow: Int?,
        selectedCol: Int?,
    ) {
        fills.forEach { fill ->
            val cell = cellByPos[CellPos(fill.row, fill.col)] ?: return@forEach
            if (!cell.editable) return@forEach
            cell.userText = fill.letter
            cell.status = CellStatus.entries.find { it.name == fill.status } ?: CellStatus.FILLED
        }
        this.hintsUsed = hintsUsed.coerceAtLeast(0)
        this.mistakes = mistakes.coerceAtLeast(0)
        if (selectedRow != null && selectedCol != null) {
            val pos = CellPos(selectedRow, selectedCol)
            if (cellByPos[pos]?.editable == true) selected = pos
        }
        refreshSolvedState()
    }

    private fun buildCells(puzzle: MathPuzzle): Map<CellPos, MathCellState> {
        val result = LinkedHashMap<CellPos, MathCellState>()
        for (r in 0 until puzzle.height) {
            for (c in 0 until puzzle.width) {
                val cell: MathCell = puzzle.cellAt(r, c)
                val pos = CellPos(r, c)
                result[pos] = MathCellState(
                    pos = pos,
                    kind = cell.kind,
                    solution = cell.solution,
                    fixedText = cell.text,
                    given = cell.given,
                )
            }
        }
        return result
    }
}
